import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Box,
  Drawer,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Add, ExpandMore } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import "./EditTechProfileForMobile.css";
import EditCertificate from "./EditCertificate";
import EditTechnicalAchievements from "./EditTechnicalAchievements";
import EditExperiences from "./EditExperience";
import EditMyTechProfilePersonalDetails from "./EditMyTechProfilePersonalDetails";
import EditMyTechProfileSkills from "./EditMyTechProfileSkills";
import EditMyTechProfileProjectDetails from "./EditMyTechProfileProjectDetails";
import EditContactDetails from "./EditContactDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  publishTechProfileAction,
  scrollToSpecificContentAction,
  setProfileState,
  techProfileGetUserPreviousDataAction,
} from "../../../actions/MyTechProfile/myTechProfileActions";
import MyTechProfile from "./MyTechProfile";
import MyTechProfileActionBar from "./MyTechProfileActionBar";
import { getPreviewBaseUrl } from "../../../utils/preview_link_handler";
import PublishLoader from "./PublishLoader";
import PublishConfirmationModal from "./PublishConfirmationModal";
import MyTechProfileSuccessModal from "./MyTechProfileSuccessModal";
import { motion } from "framer-motion";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { initialProfileData } from "../../../utils/constants";
import { redirectToPageAction } from "../../../actions/userActions";

// --- Custom Dark Theme for Mobile Drawer ---
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#ffd700",
    },
    background: {
      paper: "#1e1e1e",
      default: "#000000",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0b0b0",
    },
  },
});

const EditTechProfileForMobile = () => {
  const profileRef = useRef(null);
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const { userInfo } = useSelector((state) => state.userLogin);
  const [showLoginWarning, setShowLoginWarning] = useState(false);

  const {
    userData,
    loading: previousDataLoading,
    success: successPreviousData,
  } = useSelector((state) => state.techProfileGetPreviousUserDataStore);
  useEffect(() => {
    if (successPreviousData) {
      dispatch(setProfileState(userData?.data));
    } else {
      let profileToSet = initialProfileData;
      if (sessionStorage.getItem("mytechprofileData")) {
        profileToSet = JSON.parse(sessionStorage.getItem("mytechprofileData"));
      }
      dispatch(setProfileState(profileToSet));
    }
  }, [dispatch, successPreviousData, userData]);

  useEffect(() => {
    if (userInfo) dispatch(techProfileGetUserPreviousDataAction());
  }, [userInfo, dispatch]);

  const { loading, success, publishedData } = useSelector(
    (state) => state.techProfilePublishStore,
  );

  const [showTechAchievements, setShowTechAchievements] = useState(
    !!profile?.technicalAchievements,
  );
  const [showTechExperiences, setShowTechExperiences] = useState(
    !!profile?.experiences,
  );

  useEffect(() => {
    if (profile) {
      setShowTechAchievements(!!profile.technicalAchievements);
      setShowTechExperiences(!!profile.experiences);
    }
  }, [profile]);

  const history = useHistory();

  useEffect(() => {
    if (success) {
      setIsSuccessModalOpen(true);
      history.push(
        `/techprofile/portfolio-activation/${publishedData?.data?._id}`,
      );
    }
  }, [success]);

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const scrollTo = useCallback((section) => {
    profileRef.current?.scrollTo(section);
    dispatch(scrollToSpecificContentAction(""));
  }, []);

  const handleAddTechnicalAchievementsClick = () => {
    setShowTechAchievements(true);
    if (!profile?.technicalAchievements) {
      const initialAchievements = {
        githubUsername: "",
        currentActiveTab: "github",
        platforms: [],
      };
      dispatch(
        setProfileState({
          ...profile,
          technicalAchievements: initialAchievements,
        }),
      );
    }
  };

  const handleAddTechnicalExperiencesClick = () => {
    setShowTechExperiences(true);
  };

  const handleRemoveTechnicalAchievements = (e) => {
    e.stopPropagation();
    setShowTechAchievements(false);
    const { technicalAchievements, ...rest } = profile;
    dispatch(setProfileState(rest));
  };

  const handleRemoveTechnicalExperiences = (e) => {
    e.stopPropagation();
    setShowTechExperiences(false);
    const { experiences, ...rest } = profile;
    dispatch(setProfileState(rest));
  };

  const handleCloseSuccessModal = (event, reason) => {
    if (reason === "backdropClick") return;
    setIsSuccessModalOpen(false);
  };

  const showAddTechnicalAchievementsButton = () => (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={handleAddTechnicalAchievementsClick}
      fullWidth
      className="edittechprofilemobile-add-button"
    >
      Add Technical Achievements
    </Button>
  );
  const location = useLocation();
  const handlePublishClick = () => {
    if (userInfo) {
      setIsPublishModalOpen(true);
    } else {
      setShowLoginWarning(true);
    }
  };

  const handleGoLive = (username) => {
    // You can optionally add the username to the profile before dispatching
    const profileWithUsername = { ...profile, username };
    dispatch(publishTechProfileAction(profileWithUsername));
    setIsPublishModalOpen(false); // Close the modal
  };

  return (
    <div>
      <div>
        <MyTechProfileActionBar
          previewUrl={`${getPreviewBaseUrl}/profile/preview/${userInfo ? userInfo?._id : "1"}`}
          onPublish={handlePublishClick}
        />
      </div>
      <ThemeProvider theme={darkTheme}>
        <div
          style={{
            height: "100vh",
            overflow: "scroll",
            marginTop: "10px",
          }}
        >
          <MyTechProfile ref={profileRef} />
        </div>

        <div className="edittechprofilemobile-container">
          {/* Floating Action Button */}
          <motion.div
            className="edittechprofilemobile-fab"
            onClick={toggleDrawer(true)}
            whileHover={{
              scale: 1.15,
              boxShadow: "0 8px 30px rgba(255, 215, 0, 0.8)",
            }}
            whileTap={{ scale: 0.9 }}
            animate={{
              y: [0, -8, 0],
            }}
            transition={{
              y: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              },
              scale: {
                duration: 0.2,
              },
            }}
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <EditIcon
                sx={{ margin: "10px 10px", fontSize: "32px", color: "#000" }}
              />
            </motion.div>
          </motion.div>

          {/* Drawer Component */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            className="edittechprofilemobile-drawer"
            PaperProps={{
              className: "edittechprofilemobile-drawer-paper",
            }}
          >
            <Box
              className="edittechprofilemobile-drawer-content"
              role="presentation"
            >
              {/* Drawer Header */}
              <Box className="edittechprofilemobile-drawer-header">
                <Typography
                  variant="h6"
                  className="edittechprofilemobile-drawer-title"
                >
                  Edit Options
                </Typography>
                <IconButton
                  onClick={toggleDrawer(false)}
                  className="edittechprofilemobile-close-button"
                >
                  <CancelIcon />
                </IconButton>
              </Box>

              <div
                className="edittechprofilemobile_outercontent"
                style={{ overflow: "scroll" }}
              >
                {/* Drawer Content - Accordions */}
                <Box className="edittechprofilemobile-accordion-wrapper">
                  {/* 1. Edit Profile Details */}
                  <Accordion
                    onChange={() => scrollTo("home")}
                    defaultExpanded
                    className="edittechprofilemobile-accordion"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      className="edittechprofilemobile-accordion-summary"
                    >
                      <EditIcon className="edittechprofilemobile-accordion-icon" />
                      <Typography>Edit Profile Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="edittechprofilemobile-accordion-details">
                      <EditMyTechProfilePersonalDetails scrollTo={scrollTo} />
                    </AccordionDetails>
                  </Accordion>

                  {/* 2. Edit Technical Skills */}
                  <Accordion
                    onChange={() => scrollTo("skills")}
                    className="edittechprofilemobile-accordion"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      className="edittechprofilemobile-accordion-summary"
                    >
                      <EditIcon className="edittechprofilemobile-accordion-icon" />
                      <Typography>Edit Technical Skills</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="edittechprofilemobile-accordion-details">
                      <EditMyTechProfileSkills scrollTo={scrollTo} />
                    </AccordionDetails>
                  </Accordion>

                  {/* 3. Edit Experience (Optional) */}
                  {showTechExperiences ? (
                    <Accordion
                      onChange={() => scrollTo("experiences")}
                      className="edittechprofilemobile-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        className="edittechprofilemobile-accordion-summary"
                      >
                        <IconButton
                          size="small"
                          className="edittechprofilemobile-remove-button"
                          onClick={handleRemoveTechnicalExperiences}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                        <Typography>Edit your Experience (Optional)</Typography>
                      </AccordionSummary>
                      <AccordionDetails className="edittechprofilemobile-accordion-details">
                        <EditExperiences />
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleAddTechnicalExperiencesClick}
                      fullWidth
                      className="edittechprofilemobile-add-button edittechprofilemobile-spacing"
                    >
                      Add Experience
                    </Button>
                  )}

                  {/* 4. Edit Project Details */}
                  <Accordion
                    onChange={() => scrollTo("projects")}
                    className="edittechprofilemobile-accordion"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      className="edittechprofilemobile-accordion-summary"
                    >
                      <EditIcon className="edittechprofilemobile-accordion-icon" />
                      <Typography>Edit Project Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="edittechprofilemobile-accordion-details">
                      <EditMyTechProfileProjectDetails scrollTo={scrollTo} />
                    </AccordionDetails>
                  </Accordion>

                  {/* 5. Edit Certificate */}
                  <Accordion
                    onChange={() => scrollTo("certifications")}
                    className="edittechprofilemobile-accordion"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      className="edittechprofilemobile-accordion-summary"
                    >
                      <EditIcon className="edittechprofilemobile-accordion-icon" />
                      <Typography>Edit Certificate</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="edittechprofilemobile-accordion-details">
                      <EditCertificate
                        onStartEditing={() => scrollTo("certifications")}
                      />
                    </AccordionDetails>
                  </Accordion>

                  {/* 6. Edit Technical Achievements (Optional) */}
                  {showTechAchievements ? (
                    <Accordion
                      onChange={() => scrollTo("achievements")}
                      className="edittechprofilemobile-accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        className="edittechprofilemobile-accordion-summary"
                      >
                        <IconButton
                          size="small"
                          className="edittechprofilemobile-remove-button"
                          onClick={handleRemoveTechnicalAchievements}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                        <Typography>
                          Edit Technical Achievements (Optional)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="edittechprofilemobile-accordion-details">
                        <EditTechnicalAchievements />
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    showAddTechnicalAchievementsButton()
                  )}

                  {/* 7. Edit Contacts */}
                  <Accordion
                    onChange={() => scrollTo("contact")}
                    className="edittechprofilemobile-accordion"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      className="edittechprofilemobile-accordion-summary"
                    >
                      <EditIcon className="edittechprofilemobile-accordion-icon" />
                      <Typography>Edit Contacts</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="edittechprofilemobile-accordion-details">
                      <EditContactDetails
                        onStartEditing={() => scrollTo("certifications")}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </div>
            </Box>
          </Drawer>
        </div>
        {loading && <PublishLoader isOpen={loading} />}
        <ThemeProvider theme={darkTheme}>
          <PublishConfirmationModal
            open={isPublishModalOpen}
            onClose={() => setIsPublishModalOpen(false)}
            onGoLive={handleGoLive}
            previewUrlBase={`${getPreviewBaseUrl}/portfolio`}
            existingUsername={userData?.data?.username}
          />
        </ThemeProvider>

        <MyTechProfileSuccessModal
          open={isSuccessModalOpen}
          onClose={handleCloseSuccessModal}
          portfolioUrl={`${getPreviewBaseUrl}/portfolio/${publishedData?.data?.username}`}
        />
      </ThemeProvider>
      {previousDataLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(12px)",
            backgroundColor: "rgba(255, 255, 255, 0.04)",
            zIndex: 9999,
          }}
        >
          <CircularProgress
            size={70}
            thickness={6}
            sx={{
              color: "#fcfcfcff",
              animationDuration: "0.8s", // makes it spin faster
            }}
          />
        </Box>
      )}
      <Dialog
        open={showLoginWarning}
        onClose={() => setShowLoginWarning(false)}
        PaperProps={{
          sx: {
            background: "rgba(18, 18, 18, 0.88)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            width: "90%",
            maxWidth: "400px",
            color: "#ffffff",
            borderRadius: "18px",
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.6)",
            padding: "36px 32px",
            fontFamily: "Questrial, sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.3rem",
            padding: 0,
            marginBottom: "16px",
          }}
        >
          Login Required
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          <DialogContentText
            sx={{
              color: "#cfcfcf",
              fontSize: "0.98rem",
              lineHeight: 1.6,
              marginBottom: "32px",
            }}
          >
            Your Portfolio changes have been saved successfully.
            <br />
            <br />
            Please login to publish your Tech Portfolio. You will be redirected
            back to this page after login.
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            padding: 0,
          }}
        >
          {/* Primary Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setShowLoginWarning(false);

              const urlId = location?.pathname?.split("/")[3];

              dispatch(
                redirectToPageAction("EDIT_TECH_PROFILE_PAGE", {
                  url: `/techprofile/edit/${urlId}`,
                }),
              );

              history.push("/login");
            }}
            sx={{
              background: "linear-gradient(90deg, #8a2be2, #a855f7)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 500,
              padding: "10px 16px",
              borderRadius: "10px",
              fontSize: "0.95rem",
              "&:hover": {
                background: "linear-gradient(90deg, #7925c9, #9333ea)",
              },
            }}
          >
            Continue to Login
          </Button>

          {/* Secondary Button */}
          <Button
            fullWidth
            onClick={() => setShowLoginWarning(false)}
            sx={{
              backgroundColor: "transparent",
              border: "1px solid rgba(138, 43, 226, 0.6)",
              color: "#dcdcdc",
              textTransform: "none",
              padding: "10px 16px",
              borderRadius: "10px",
              fontSize: "0.9rem",
              "&:hover": {
                backgroundColor: "rgba(138, 43, 226, 0.08)",
                borderColor: "#8a2be2",
                color: "#ffffff",
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditTechProfileForMobile;
